"use client";

import { useConnectWallet, useSetChain } from "@web3-onboard/react";
import { useCallback, useEffect } from "react";

import { CHAINS } from "@/constants";
import { Chain } from "@/types";

export const useChain = (): {
  chain: Chain;
  isBusy: boolean;
  setChain: (targetChain: Chain) => Promise<void>;
} => {
  const [{ wallet }] = useConnectWallet();
  const [{ connectedChain, settingChain }] = useSetChain();
  const chain = connectedChain
    ? CHAINS.find(({ id }) => id === BigInt(connectedChain.id)) ?? CHAINS[0]
    : CHAINS[0];
  const setChain = useCallback(
    async (targetChain: Chain) => {
      const chainIdHex = `0x${targetChain.id.toString(16)}`;

      if (!wallet?.provider) {
        console.warn("No wallet provider available");
        return;
      }

      try {
        await wallet.provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: chainIdHex }],
        });
      } catch (switchError: unknown) {
        const hasErrorCode =
          switchError &&
          typeof switchError === "object" &&
          "code" in switchError &&
          (switchError as { code: number }).code === 4902;

        if (hasErrorCode) {
          try {
            await wallet.provider.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: chainIdHex,
                  chainName: targetChain.title,
                  nativeCurrency: {
                    decimals: 18,
                    name: targetChain.token,
                    symbol: targetChain.token,
                  },
                  rpcUrls: [targetChain.url],
                },
              ],
            });
          } catch (addError) {
            console.error("Failed to add chain:", addError);
            throw addError;
          }
        } else {
          console.error("Failed to switch chain:", switchError);
          throw switchError;
        }
      }
    },
    [wallet]
  );

  useEffect(() => {
    if (
      wallet &&
      connectedChain &&
      !CHAINS.find(({ id }) => id === BigInt(connectedChain.id))
    ) {
      setTimeout(async () => {
        const targetChain = CHAINS[0];

        try {
          console.log("Attempting to switch to chain:", targetChain.title);
          await setChain(targetChain);
          console.log("Successfully switched to chain");
        } catch (error) {
          console.error("Failed to switch/add chain:", error);

          alert(
            `Please manually add the ${targetChain.title} network to your wallet.\n\n` +
              `Network: ${targetChain.title}\n` +
              `Chain ID: ${targetChain.id}\n` +
              `RPC URL: ${targetChain.url}`
          );
        }
      }, 1000);
    }
  }, [connectedChain, wallet, setChain]);

  return {
    chain,
    isBusy: settingChain,
    setChain,
  };
};
